import type { LocalMatchHost } from '@signalclash/shared';

import type { DebugOverlay } from '../debug/DebugOverlay.js';
import type { PerfOverlay } from '../debug/PerfOverlay.js';
import type { Renderer } from '../rendering/Renderer.js';

// Per-frame heartbeat. Phaser's scene supplies the rAF; ClientLoop owns the order:
// 1. advance the host's accumulator (which may run 0+ sim ticks),
// 2. let the renderer interpolate to the current wall-clock,
// 3. update debug readout.
// The renderer NEVER writes back to the simulation. The host NEVER reads from sprites.
export class ClientLoop {
  private frameCount = 0;
  private fpsAccumulatorMs = 0;
  private fpsLastSampleMs: number | null = null;
  private fps = 0;
  // Pause gate — when true, skip host.update so the simulation accumulator
  // doesn't catch up on resume. Render still runs so the world remains
  // visually drawn (paused on the last snapshot).
  private paused = false;
  // Match-start gate — held true during the 3-2-1 countdown so the world
  // is frozen until "FIGHT". Tracked separately from `paused` so the
  // PauseController's tab-away resume can't unfreeze a still-counting
  // match (and vice-versa).
  private startLocked = false;

  constructor(
    private readonly host: LocalMatchHost,
    private readonly renderer: Renderer,
    private readonly debug: DebugOverlay,
    private readonly perfOverlay: PerfOverlay | null = null,
  ) {}

  setPaused(paused: boolean): void {
    this.paused = paused;
  }

  setStartLocked(locked: boolean): void {
    this.startLocked = locked;
  }

  tick(nowMs: number): void {
    if (!this.paused && !this.startLocked) this.host.update(nowMs);
    this.renderer.render(nowMs);
    this.sampleFps(nowMs);
    this.debug.setFps(this.fps);
    if (this.perfOverlay !== null) {
      this.perfOverlay.setFps(this.fps);
      this.perfOverlay.recordFrame(nowMs);
    }
  }

  private sampleFps(nowMs: number): void {
    if (this.fpsLastSampleMs === null) {
      this.fpsLastSampleMs = nowMs;
      return;
    }
    this.frameCount += 1;
    this.fpsAccumulatorMs += nowMs - this.fpsLastSampleMs;
    this.fpsLastSampleMs = nowMs;
    if (this.fpsAccumulatorMs >= 500) {
      this.fps = Math.round((this.frameCount * 1000) / this.fpsAccumulatorMs);
      this.frameCount = 0;
      this.fpsAccumulatorMs = 0;
    }
  }
}
